import React from 'react';
import { ArrowRight, Phone, Check, Anchor, Shield, Waves, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { imgUrl } from '../utils/imgUrl';
import { LegalPolicies } from '../components/LegalPolicies';

const About = () => {
  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <SEO
        title="About Us | Rezvani Furniture Studio"
        description="Rezvani Furniture Studio offers premium in-pool and outdoor furniture for families, resorts, and commercial properties — durable, weather-resistant, and built to last."
        canonical="https://rezvanifurniturestudio.com/about"
      />

      {/* Hero Section */}
      <section
        className="relative h-72 sm:h-96 bg-cover bg-center mb-14 sm:mb-20"
        style={{
          backgroundImage: `url(${imgUrl('https://images.pexels.com/photos/11846970/pexels-photo-11846970.jpeg?w=1920&q=80')})`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 to-gray-900/60" />
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-3 sm:mb-4">About Us</h1>
            <p className="text-base sm:text-2xl text-gray-200">Premium Outdoor &amp; Poolside Furniture</p>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* About Rezvani Furniture Studio */}
        <section className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">About Rezvani Furniture Studio</h2>
          <div className="space-y-4 text-base sm:text-lg text-gray-700 leading-relaxed">
            <p>
              At Rezvani Furniture Studio, we believe outdoor living should be as comfortable and stylish as the spaces inside your home. Our mission is simple: provide high-quality, durable outdoor and poolside furniture that helps families, resorts, and homeowners create unforgettable experiences.
            </p>
            <p>
              We specialize in premium in-pool and outdoor furniture designed to withstand the elements while delivering exceptional comfort and performance. From pool loungers and tanning ledge furniture to outdoor seating solutions, every product we offer is carefully selected for its quality, durability, and design.
            </p>
            <p data-testid="authorized-retailer-statement">
              <strong>Rezvani Furniture Studio is an authorized online retailer of Tenjam&reg; SPLASH Series in-pool furniture.</strong> Tenjam&reg; and the SPLASH Series are trademarks of their respective owner; all product designs and patents belong to the manufacturer. We are an independent, U.S.-based retail business operating out of Chandler, Arizona, and are not the manufacturer. When you order from us, your purchase, payment, and customer support are handled directly by Rezvani Furniture Studio.
            </p>
          </div>
        </section>

        {/* Built for Outdoor Living */}
        <section className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Built for Outdoor Living</h2>
          <div className="space-y-4 text-base sm:text-lg text-gray-700 leading-relaxed">
            <p>
              Outdoor furniture faces some of the harshest conditions imaginable&mdash;sun, water, heat, chemicals, and everyday use. That&rsquo;s why we focus on products made from premium materials that are UV-resistant, weather-resistant, and built to last.
            </p>
            <p>
              Many of the products we offer are proudly made in the USA and manufactured using recyclable materials, allowing our customers to enjoy beautiful outdoor spaces while making environmentally conscious choices.
            </p>
          </div>
        </section>

        {/* Customer First */}
        <section className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Customer First</h2>
          <div className="space-y-4 text-base sm:text-lg text-gray-700 leading-relaxed">
            <p>
              At Rezvani Furniture Studio, customer satisfaction is at the center of everything we do. We understand that purchasing outdoor furniture is an investment, and our team is committed to helping customers find the right products for their space, lifestyle, and budget.
            </p>
            <p>
              Whether you&rsquo;re furnishing a backyard oasis, commercial pool area, resort, or vacation property, we&rsquo;re here to provide expert guidance and exceptional service every step of the way.
            </p>
          </div>
        </section>

        {/* Why Choose */}
        <section className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-5">Why Choose Rezvani Furniture Studio?</h2>
          <ul className="space-y-3">
            {[
              'Premium Built',
              'Expertly Crafted',
              'Engineered for Performance',
              'Built to Last',
              'Commercial Grade Construction',
              'Premium outdoor and poolside furniture',
              'Durable, weather-resistant construction',
              'Made in America options available',
              '100% recyclable materials on select products',
              'Fast and reliable customer support',
              'Secure online ordering',
              'Products designed for comfort, style, and longevity',
            ].map((point) => (
              <li key={point} className="flex items-start gap-3 text-base sm:text-lg text-gray-700">
                <Check size={22} className="text-cyan-600 mt-1 flex-shrink-0" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Our Commitment */}
        <section className="mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Our Commitment</h2>
          <div className="space-y-4 text-base sm:text-lg text-gray-700 leading-relaxed">
            <p>
              We are committed to offering innovative products that combine function, comfort, and modern design. Every item we sell is selected with our customers in mind, ensuring you receive products that enhance your outdoor lifestyle for years to come.
            </p>
            <p>
              Thank you for choosing Rezvani Furniture Studio. We look forward to helping you create the perfect outdoor space.
            </p>
          </div>
        </section>

        {/* Signature / Contact Block */}
        <section className="mb-14 sm:mb-20 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-6 sm:p-8 border border-cyan-100">
          <div className="text-center space-y-1">
            <p className="text-lg sm:text-xl font-semibold text-gray-900">Rezvani Furniture Studio</p>
            <p className="text-sm sm:text-base text-gray-600">Premium Outdoor &amp; Poolside Furniture</p>
            <a
              href="tel:+16238888644"
              className="inline-flex items-center gap-2 text-cyan-700 hover:text-cyan-800 font-semibold mt-2"
            >
              <Phone size={18} />
              (623) 888-8644
            </a>
          </div>
        </section>
      </div>

      {/* Ballast System (Tenjam, third-person) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <section className="mb-14 sm:mb-20">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 via-slate-900 to-cyan-950 text-white p-8 sm:p-12 lg:p-16 shadow-2xl">
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 1440 600" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 300C240 200 480 400 720 300C960 200 1200 400 1440 300V600H0V300Z" fill="url(#aboutWaveGrad)" />
                <defs>
                  <linearGradient id="aboutWaveGrad" x1="0" y1="0" x2="1440" y2="600" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#22d3ee" />
                    <stop offset="1" stopColor="#3b82f6" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-400/30 backdrop-blur-sm mb-5">
                  <Sparkles size={16} className="text-cyan-300" />
                  <span className="text-xs font-semibold text-cyan-300 uppercase tracking-[0.2em]">Patent-Pending Innovation</span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-5 leading-tight">
                  The <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-400">Tenjam Ballast Stabilization System</span>
                </h2>
                <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-4">
                  Tenjam&rsquo;s integrated, <strong className="text-white">patent-pending Ballast Stabilization System</strong> sets these in-pool loungers apart from every other product on the market. Instead of outdated fill-and-drain methods, Tenjam&rsquo;s technology automatically locks each piece securely onto a tanning ledge or Baja shelf &mdash; no sand, no water filling, no hassle.
                </p>
                <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
                  Because there are no hollow cavities, no fill ports, and no standing water inside, internal algae growth is eliminated by design. Crafted from commercial-grade, UV-stable, antimicrobial material, every Tenjam piece stays cleaner, safer, and more reliable &mdash; season after season.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: Anchor, title: 'Anchored & Stable', desc: 'Locks securely in place — no drift, no flip.' },
                  { icon: Shield, title: 'Algae-Free Design', desc: 'No hollow cavities or fill ports = nothing to grow inside.' },
                  { icon: Waves, title: 'No Fill Required', desc: 'Skip the sand and water steps competitors rely on.' },
                  { icon: Sparkles, title: 'Cleaner. Safer.', desc: 'Sanitary and maintenance-free, season after season.' },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300" data-testid={`about-ballast-feature-${i}`}>
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                      <f.icon size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-white mb-1">{f.title}</h3>
                      <p className="text-sm text-gray-400 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-3xl p-8 sm:p-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Ready to Transform Your Pool?</h2>
          <p className="text-base sm:text-xl text-gray-600 mb-6 sm:mb-8">
            Let&rsquo;s create the perfect outdoor oasis together
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-base sm:text-lg font-semibold rounded-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 space-x-2"
          >
            <span>Get Started</span>
            <ArrowRight size={20} className="sm:w-[22px] sm:h-[22px]" />
          </Link>
        </section>
      </div>

      {/* Legal: Privacy Policy & Terms of Service (inconspicuous, collapsible) */}
      <LegalPolicies />
    </div>
  );
};

export default About;
